import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import Utils, {formatMoney} from "../../../../../../core/services/utils/utils";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {UserPromotionRedeemListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {Row} from "reactstrap";
import PartnerDataGrid from "../../../../../containers/data-grid";
import UserPromotionsListSearchSection from "../../../../../components/search-sections/user-promotion-redeems-search-section";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {useAppSelector} from "../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'UserPromotionsViewDataGrid',
    version: 1,
}

const CustomerPromotionsView = () => {
    const [data, setData] = useState([]);
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('customer-promotions-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerPromotions(defaultCustomer.code));
        configureBehavioralTool((tags) => tags.customerPromotions(defaultCustomer.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer]);

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Code",
                name: UserPromotionRedeemListDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Amount",
                name: UserPromotionRedeemListDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 90,
            },
            {
                title: "Card Number",
                name: UserPromotionRedeemListDataGridCellKeys.cardNumber,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 170,
            },
            {
                title: "Redeem Date & Time",
                name: UserPromotionRedeemListDataGridCellKeys.redeemDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
        ], [])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets the list of drivers by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            CustomerId: defaultCustomer.id,
            keyword: args.filters?.keyword?.toString(),
            StartDateTime: args.filters?.startDate,
            EndDateTime: args.filters?.endDate,
            pagination: args.paginationInfo,
        };
        const response = await Api.getUserRedeemHistory(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [defaultCustomer?.id, isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportCustomerPromotionUsages = operatorAccessLevels.packages.sales.exportCustomerPromotionUsages;

    /**
     * Returns the object for exporting the list of drivers by preparing the title and body of the csv file
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Promotion Redeems of ${defaultCustomer.name}`,
            body: list?.map((e, index) => ({
                '': `${index + 1}`,
                'Code': e.code ?? '--',
                'Card Number': e.cardNumber ?? '--',
                'Amount': formatMoney(e.amount) ?? '--',
                'Redeem Date & Time': e.redeemedDateTime ? DateUtils.serverToLocal(e.redeemedDateTime).format(DateUtils.dateTimeFormat) : '',
            })) ?? [],
        }
    }, [defaultCustomer?.name])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const rows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [UserPromotionRedeemListDataGridCellKeys.code]: item.code,
                [UserPromotionRedeemListDataGridCellKeys.amount]: item.amount,
                [UserPromotionRedeemListDataGridCellKeys.cardNumber]: Utils.formatCardNumber(item.cardNumber),
                [UserPromotionRedeemListDataGridCellKeys.redeemDateTime]: item.redeemedDateTime,
            },
        }))
    }, [data])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center mt-4 mb-3 pr-2'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-2'}>
                    {defaultCustomer?.name ?? "Customer"}'s Promotion Redeem History
                </h2>
                <div/>
            </Row>
            <UserPromotionsListSearchSection searchData={searchData}/>
            <div className={'mb-3'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportCustomerPromotionUsages ? exportToCsv : undefined}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}

export default CustomerPromotionsView;
